import { queryOptions, useQuery } from '@tanstack/react-query';
import { LibraryService } from '../service/LibraryService';

const LIBRARIES_STALE_TIME_MS = 60 * 1000;

type LibraryScope = 'System' | 'Organization' | 'Private' | 'Space';

const librariesQueries = {
  system: () =>
    queryOptions({
      queryKey: ['libraries', 'system'] as const,
      queryFn: () => LibraryService.getSystemLibraries(),
      staleTime: LIBRARIES_STALE_TIME_MS,
    }),
  organization: () =>
    queryOptions({
      queryKey: ['libraries', 'organization'] as const,
      queryFn: () => LibraryService.getOrganizationLibraries(),
      staleTime: LIBRARIES_STALE_TIME_MS,
    }),
  private: () =>
    queryOptions({
      queryKey: ['libraries', 'private'] as const,
      queryFn: () => LibraryService.getPrivateLibraries(),
      staleTime: LIBRARIES_STALE_TIME_MS,
    }),
  space: (spaceId: string) =>
    queryOptions({
      queryKey: ['libraries', 'space', spaceId] as const,
      queryFn: () => LibraryService.getSpaceLibraries(spaceId),
      staleTime: LIBRARIES_STALE_TIME_MS,
    }),
};

const resolveOptions = (
  scope: LibraryScope,
  spaceId: string | null | undefined,
) => {
  if (scope === 'System') return librariesQueries.system();
  if (scope === 'Organization') return librariesQueries.organization();
  if (scope === 'Space' && spaceId) return librariesQueries.space(spaceId);
  return librariesQueries.private();
};

const useLibrariesQuery = (
  scope: LibraryScope | null | undefined,
  spaceId?: string | null,
  opts?: { enabled?: boolean },
) => {
  const options = scope
    ? resolveOptions(scope, spaceId)
    : librariesQueries.private();
  const isSpaceScopeWithoutId = scope === 'Space' && !spaceId;
  return useQuery({
    ...options,
    enabled:
      Boolean(scope) && !isSpaceScopeWithoutId && (opts?.enabled ?? true),
  });
};

export type { LibraryScope };
export { librariesQueries, useLibrariesQuery };
